import React from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { PrescriptionGroup, Patient, PrescriptionSource } from "common/models/types";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import Icon from "components/atoms/Icon";
import { ConfirmManager } from "components/organisms/CommonUI/ConfirmPopup/ConfirmManager";
import { SOURCE_ICON } from "common/constants/prescriptionSource";

interface PrescriptionGroupListProps {
  patient: Patient;
  editingPrescriptionGroup?: PrescriptionGroup | null;
  onEditPrescriptionGroup: (
    groupId: string,
    prescriptionDate: Date | string,
    prescriptions: any[]
  ) => void;
  onDeletePrescriptionGroup: (groupId: string, prescriptions: any[]) => void;
  onAddPrescription: () => void;
}

const PrescriptionItem = styled.div`
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  background-color: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  margin-bottom: 16px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px;
  text-align: center;
`;

const PrescriptionGroupList: React.FC<PrescriptionGroupListProps> = ({
  patient,
  editingPrescriptionGroup,
  onEditPrescriptionGroup,
  onDeletePrescriptionGroup,
  onAddPrescription,
}) => {
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
        <Text textType="Subhead1">처방전 목록</Text>
        <Button
          buttonType="button"
          color="Primary"
          size="Small"
          type="Text"
          variant="Contained"
          onClick={onAddPrescription}
        >
          처방전 추가
        </Button>
      </div>

      {/* 처방전 그룹 목록 */}
      {patient.prescriptionGroups && patient.prescriptionGroups.length > 0 ? (
        <div>
          {patient.prescriptionGroups
            .filter(
              (group) =>
                !editingPrescriptionGroup || group.groupId !== editingPrescriptionGroup.groupId
            )
            .map((group) => (
              <PrescriptionItem key={group.groupId}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "12px",
                  }}
                >
                  <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[900]}>
                    처방전 ({new Date(group.prescriptionDate).toLocaleDateString()})
                  </Text>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <Button
                      buttonType="button"
                      color="Primary"
                      size="Small"
                      type="IconWithText"
                      variant="Outlined"
                      icon="Pencil"
                      left={true}
                      onClick={() =>
                        onEditPrescriptionGroup(
                          group.groupId,
                          group.prescriptionDate,
                          group.prescriptions
                        )
                      }
                    >
                      수정
                    </Button>
                    <Button
                      buttonType="button"
                      color="Negative"
                      size="Small"
                      type="IconWithText"
                      variant="Outlined"
                      icon="Trash"
                      left={true}
                      onClick={() =>
                        ConfirmManager.show({
                          title: "처방전 삭제",
                          body: "이 처방전을 삭제하시겠습니까?",
                          buttons: [
                            <Button
                              key="confirm"
                              buttonType="button"
                              color="Negative"
                              size="Small"
                              type="Text"
                              variant="Contained"
                              onClick={() => {
                                onDeletePrescriptionGroup(group.groupId, group.prescriptions);
                                ConfirmManager.hide();
                              }}
                            >
                              삭제
                            </Button>,
                            <Button
                              key="cancel"
                              buttonType="button"
                              color="Secondary"
                              size="Small"
                              type="Text"
                              variant="Outlined"
                              onClick={() => ConfirmManager.hide()}
                            >
                              취소
                            </Button>,
                          ],
                        })
                      }
                    />
                  </div>
                </div>

                {/* 처방전 테이블 형태로 표시 */}
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                    borderRadius: "8px",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                    marginTop: "12px",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        backgroundColor: COLOR_SYSTEM.get("Blue")?.[50],
                        textAlign: "left",
                      }}
                    >
                      <th
                        style={{
                          padding: "10px 12px",
                          fontWeight: "600",
                          color: COLOR_SYSTEM.get("Blue")?.[700],
                          fontSize: "14px",
                          borderBottom: `1px solid ${COLOR_SYSTEM.get("Gray")?.[200]}`,
                        }}
                      >
                        코드번호
                      </th>
                      <th
                        style={{
                          padding: "10px 12px",
                          fontWeight: "600",
                          color: COLOR_SYSTEM.get("Blue")?.[700],
                          fontSize: "14px",
                          borderBottom: `1px solid ${COLOR_SYSTEM.get("Gray")?.[200]}`,
                        }}
                      >
                        의약품명
                      </th>
                      <th
                        style={{
                          padding: "10px 12px",
                          fontWeight: "600",
                          color: COLOR_SYSTEM.get("Blue")?.[700],
                          fontSize: "14px",
                          textAlign: "center",
                          borderBottom: `1px solid ${COLOR_SYSTEM.get("Gray")?.[200]}`,
                        }}
                      >
                        1회 투약량
                      </th>
                      <th
                        style={{
                          padding: "10px 12px",
                          fontWeight: "600",
                          color: COLOR_SYSTEM.get("Blue")?.[700],
                          fontSize: "14px",
                          textAlign: "center",
                          borderBottom: `1px solid ${COLOR_SYSTEM.get("Gray")?.[200]}`,
                        }}
                      >
                        1일 투약횟수
                      </th>
                      <th
                        style={{
                          padding: "10px 12px",
                          fontWeight: "600",
                          color: COLOR_SYSTEM.get("Blue")?.[700],
                          fontSize: "14px",
                          textAlign: "center",
                          borderBottom: `1px solid ${COLOR_SYSTEM.get("Gray")?.[200]}`,
                        }}
                      >
                        투약일수
                      </th>
                      <th
                        style={{
                          padding: "10px 12px",
                          fontWeight: "600",
                          color: COLOR_SYSTEM.get("Blue")?.[700],
                          fontSize: "14px",
                          borderBottom: `1px solid ${COLOR_SYSTEM.get("Gray")?.[200]}`,
                        }}
                      >
                        용법
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.prescriptions.map((prescription, idx) => (
                      <tr
                        key={prescription.id || `table-row-${idx}`}
                        style={{
                          backgroundColor: "white",
                        }}
                      >
                        <td
                          style={{
                            padding: "12px",
                            borderBottom:
                              idx === group.prescriptions.length - 1
                                ? "none"
                                : `1px solid ${COLOR_SYSTEM.get("Gray")?.[200]}`,
                            color: COLOR_SYSTEM.get("Gray")?.[800],
                            fontSize: "14px",
                          }}
                        >
                          {prescription.insuranceCode || "-"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            borderBottom:
                              idx === group.prescriptions.length - 1
                                ? "none"
                                : `1px solid ${COLOR_SYSTEM.get("Gray")?.[200]}`,
                            color: COLOR_SYSTEM.get("Gray")?.[800],
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          {prescription.medicationName}
                          {prescription.source && (prescription.source in SOURCE_ICON) && (
                            <span title={SOURCE_ICON[prescription.source as PrescriptionSource].label} style={{ marginLeft: 4, display: "inline-flex", verticalAlign: "middle" }}>
                              <Icon name={SOURCE_ICON[prescription.source as PrescriptionSource].name} size={13} color={COLOR_SYSTEM.get("Gray")?.[400]} />
                            </span>
                          )}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            borderBottom:
                              idx === group.prescriptions.length - 1
                                ? "none"
                                : `1px solid ${COLOR_SYSTEM.get("Gray")?.[200]}`,
                            textAlign: "center",
                            color: COLOR_SYSTEM.get("Gray")?.[800],
                            fontSize: "14px",
                          }}
                        >
                          {prescription.dosagePerAdministration
                            ? `${prescription.dosagePerAdministration}${
                                prescription.dosageUnit || ""
                              }`
                            : "0"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            borderBottom:
                              idx === group.prescriptions.length - 1
                                ? "none"
                                : `1px solid ${COLOR_SYSTEM.get("Gray")?.[200]}`,
                            textAlign: "center",
                            color: COLOR_SYSTEM.get("Gray")?.[800],
                            fontSize: "14px",
                          }}
                        >
                          {prescription.timesPerDay || 0}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            borderBottom:
                              idx === group.prescriptions.length - 1
                                ? "none"
                                : `1px solid ${COLOR_SYSTEM.get("Gray")?.[200]}`,
                            textAlign: "center",
                            color: COLOR_SYSTEM.get("Gray")?.[800],
                            fontSize: "14px",
                          }}
                        >
                          {prescription.daysOfAdministration || 0}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            borderBottom:
                              idx === group.prescriptions.length - 1
                                ? "none"
                                : `1px solid ${COLOR_SYSTEM.get("Gray")?.[200]}`,
                            color: COLOR_SYSTEM.get("Gray")?.[800],
                            fontSize: "14px",
                          }}
                        >
                          {prescription.usageInstruction || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </PrescriptionItem>
            ))}
        </div>
      ) : (
        !editingPrescriptionGroup && (
          <EmptyState>
            <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[500]}>
              등록된 처방전이 없습니다
            </Text>
            <Button
              buttonType="button"
              color="Primary"
              size="Small"
              type="Text"
              variant="Contained"
              onClick={onAddPrescription}
              style={{ marginTop: "16px" }}
            >
              처방전 추가
            </Button>
          </EmptyState>
        )
      )}
    </>
  );
};

export default React.memo(PrescriptionGroupList);
